import React, { useState, useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Home, Settings, LogOut, Info, Table, Menu, X, BadgeCheck } from "lucide-react";
import OfflineBanner from "@/components/OfflineBanner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

import HomePage from "@/pages/HomePage";
import { Toaster } from "@/components/ui/toaster";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import ProtectedRoute from "@/components/ProtectedRoute";

// ✅ Lazy-компоненти
const FeedbackMessage = lazy(() => import("@/components/feedback/FeedbackMessage"));
const InfoPage = lazy(() => import("@/pages/InfoPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ChangelogPage = lazy(() => import("@/pages/ChangelogPage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const BenefitsPage = lazy(() => import("@/pages/BenefitsPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminTablePage = lazy(() => import("@/pages/AdminTablePage"));
const AdminPrintPage = lazy(() => import("@/pages/AdminPrintPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const PrintSchedulePublic = lazy(() => import("@/pages/PrintSchedulePublic"));
const LoginDialog = lazy(() => import("@/components/admin/LoginDialog"));
const ThemeToggle = lazy(() => import("@/components/ThemeToggle"));

// глобальний антикраш
window.addEventListener("error", (e) => {
  console.error("🔴 Global error:", e);
});

const AppLayout = ({ isAdmin, onLogout }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleCloseMenu = () => setMobileMenuOpen(false);

  return (
    <div className="flex flex-col min-h-screen">
    <OfflineBanner />
      <nav className="no-print bg-background/80 shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <Link
            to="/date"
            className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent"
          >
            Тальянківський коледж
          </Link>

          {/* Десктоп-меню */}
          <div className="hidden md:flex space-x-2 items-center">
            <Button
              variant={
                location.pathname.startsWith("/date") ? "default" : "ghost"
              }
              asChild
            >
              <Link to="/date">
                <Home className="mr-2 h-4 w-4" /> Головна
              </Link>
            </Button>
            <Button
              variant={location.pathname === "/info" ? "default" : "ghost"}
              asChild
            >
              <Link to="/info">
                <Info className="mr-2 h-4 w-4" /> Info
              </Link>
            </Button>

            <Button
              variant={location.pathname === "/benefits" ? "default" : "ghost"}
              asChild
            >
              <Link to="/benefits">
                <BadgeCheck className="mr-2 h-4 w-4" />
                Переваги
              </Link>
            </Button>


<Suspense fallback={null}>
  <ThemeToggle />
</Suspense>


            {/* ✅ адмінські пункти бачать лише isAdmin */}
            {isAdmin && (
              <>
                <Button
                  variant={location.pathname === "/admin" ? "default" : "ghost"}
                  asChild
                >
                  <Link to="/admin">
                    <Settings className="mr-2 h-4 w-4" /> Адмін
                  </Link>
                </Button>
                <Button
                  variant={
                    location.pathname === "/AdminTable" ? "default" : "ghost"
                  }
                  asChild
                >
                  <Link to="/AdminTable">
                    <Table className="mr-2 h-4 w-4" /> Таблиця
                  </Link>
                </Button>
                <Button variant="ghost" onClick={onLogout}>
                  <LogOut className="mr-2 h-4 w-4" /> Вийти
                </Button>
              </>
            )}
          </div>

{/* Мобільне меню */}
<div className="flex md:hidden">
  <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
    <SheetTrigger asChild>
      <Button variant="ghost" size="icon" aria-label="Меню">
        <Menu className="h-6 w-6" />
      </Button>
    </SheetTrigger>

    <SheetContent side="right" className="w-64 backdrop-blur-md shadow-lg">
      <SheetHeader className="flex flex-row items-center justify-between space-y-0 text-left">
        <SheetTitle className="text-base font-semibold">Меню</SheetTitle>
        <SheetClose asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Закрити меню"
            className="-mr-2"
          >
            <X className="h-5 w-5" />
          </Button>
        </SheetClose>
      </SheetHeader>

      {/* Кнопки мобільного меню */}
      <div className="flex flex-col space-y-2 mt-4">
        <Button variant="ghost" asChild onClick={handleCloseMenu}>
          <Link to="/date">
            <Home className="mr-2 h-4 w-4" /> Головна
          </Link>
        </Button>
        <Button variant="ghost" asChild onClick={handleCloseMenu}>
          <Link to="/info">
            <Info className="mr-2 h-4 w-4" /> Info
          </Link>
        </Button>

        <Button variant="ghost" asChild onClick={handleCloseMenu}>
          <Link to="/benefits">
            <BadgeCheck className="mr-2 h-4 w-4" />
            Переваги
          </Link>
        </Button>

        {/* Роздільник */}
        <hr className="my-4 border-gray-300 dark:border-gray-700" />

        {/* Перемикач теми */}
        <div className="w-full flex justify-center items-center">
          <ThemeToggle />
        </div>
       {isAdmin && (
         <>
           <Button variant="ghost" asChild onClick={handleCloseMenu}>
             <Link to="/admin">
               <Settings className="mr-2 h-4 w-4" /> Адмін
             </Link>
           </Button>
           <Button variant="ghost" asChild onClick={handleCloseMenu}>
             <Link to="/AdminTable">
               <Table className="mr-2 h-4 w-4" /> Таблиця
             </Link>
           </Button>
           <Button variant="ghost" onClick={onLogout}>
             <LogOut className="mr-2 h-4 w-4" /> Вийти
           </Button>
         </>
       )}
      </div>
    </SheetContent>
  </Sheet>
</div>
</div>

      </nav>

      <main className="flex-grow">
        <AnimatePresence mode="wait">
<Routes location={location}>
            {/*
              Redirect root ("/") to user's preferred start path if present in localStorage.
              Key: 'preferred_start' — should be full path like '/group/A-11' or '/teacher/ivanov'
              Fallback: '/date'
            */}
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    (typeof window !== 'undefined' &&
                      (function () {
                        try {
                          const v = localStorage.getItem('preferred_start');
                          return v && typeof v === 'string' && v.startsWith('/') ? v : '/date';
                        } catch (e) {
                          return '/date';
                        }
                      })()) ||
                    '/date'
                  }
                  replace
                />
              }
            />
            <Route path="/date/*" element={<HomePage initialMode="by_date" />} />
            <Route
              path="/group/:slug?"
              element={<HomePage initialMode="by_group" />}
            />
            <Route
              path="/teacher/:slug?"
              element={<HomePage initialMode="by_teacher" />}
            />
<Route
  path="/admin/print-schedule"
  element={
    <Suspense fallback={<div className="p-6 text-center text-muted-foreground">Завантаження...</div>}>
      <AdminPrintPage />
    </Suspense>
  }
/>

<Route
  path="/admin"
  element={
    <ProtectedRoute isAdmin={isAdmin}>
      <Suspense fallback={<div>Завантаження...</div>}>
        <AdminPage />
      </Suspense>
    </ProtectedRoute>
  }
/>


<Route
  path="/AdminTable"
  element={
    <ProtectedRoute isAdmin={isAdmin}>
      <Suspense fallback={<div className="p-6 text-center text-muted-foreground">Завантаження...</div>}>
        <AdminTablePage />
      </Suspense>
    </ProtectedRoute>
  }
/>
            <Route path="/admin/print-schedule" element={<AdminPrintPage />} />
            <Route
  path="/info"
  element={
    <Suspense fallback={<div className="p-6 text-center text-muted-foreground">Завантаження...</div>}>
      <InfoPage />
    </Suspense>
  }
/>
<Route
  path="/about"
  element={
    <Suspense fallback={<div className="p-6 text-center text-muted-foreground">Завантаження...</div>}>
      <AboutPage />
    </Suspense>
  }
/>
<Route
  path="/support"
  element={
    <Suspense fallback={<div className="p-6 text-center text-muted-foreground">Завантаження...</div>}>
      <SupportPage />
    </Suspense>
  }
/>
<Route
  path="/changelog"
  element={
    <Suspense fallback={<div className="p-6 text-center text-muted-foreground">Завантаження...</div>}>
      <ChangelogPage />
    </Suspense>
  }
/>
<Route
  path="/Benefits"
  element={
    <Suspense fallback={<div className="p-6 text-center text-muted-foreground">Завантаження...</div>}>
      <BenefitsPage />
    </Suspense>
  }
/>

            <Route
  path="/auth/callback"
  element={
    <Suspense fallback={<div className="p-6 text-center text-muted-foreground">Завантаження...</div>}>
      <AuthCallback />
    </Suspense>
  }
/>

<Route path="*" element={
  <Suspense fallback={null}>
    <NotFoundPage />
  </Suspense>
} />

</Routes>
</AnimatePresence> 
      <Toaster />
<div className="no-print">
  {import.meta.env.VITE_FEEDBACK_ENABLED === 'true' && (
    <Suspense fallback={null}>
      <FeedbackMessage />
    </Suspense>
  )}
</div>
</main>

    </div>
  );
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false); // залишено для сумісності, якщо десь ще використовується
  const [isAdmin, setIsAdmin] = useState(false);

  // при старті перевіряємо сесію
  useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        setIsLoggedIn(true);

        const { data } = await supabase
          .from("admins")
          .select("*")
          .eq("email", session.user.email)
          .maybeSingle();

        if (data) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
          await supabase.auth.signOut();
        }
      } else {
        setIsLoggedIn(false);
        setIsAdmin(false);
      }
    };

    checkSession();
  }, []);

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    setIsAdmin(true); // ✅ бо LoginDialog вже перевіряє admins
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    setIsAdmin(false);
  };

  return (
    <Router>
      <Routes>
        {/* 🔑 прихований маршрут для логіну */}
<Route
  path="/login"
  element={
    <Suspense fallback={<div className="p-6 text-center text-muted-foreground">Завантаження...</div>}>
      <LoginDialog
        isOpen={true}
        onOpenChange={() => {}}
        onLoginSuccess={handleLoginSuccess}
      />
    </Suspense>
  }
/>


        {/* всі інші сторінки */}
        <Route
          path="*"
          element={<AppLayout isAdmin={isAdmin} onLogout={handleLogout} />}
        />
      </Routes>
    </Router>
  );
}



if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .catch((err) => console.warn('❌ Помилка SW:', err));
  });
}



export default App;
